import { IconButton, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Chat as ChatIcon } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import { CHATBOT_IFRAME_URL } from '../../../constants';
import { useStore } from '../../../zustand/stores';
import { CopilotDisplayMode } from '../../../zustand/stores/copilot';
import {
  createIframeMessageHandler,
} from '../../ArticleRightSideBar/iframeMessageHandler';

import css from './index.module.scss';

type ArticleRightPanelProps = {
  selfArticleRedirect: (url: string, shallow?: boolean, targetLocale?: string) => void;
  isCollapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
};

const noop = () => undefined;

export const ArticleRightPanel = ({
  selfArticleRedirect,
  isCollapsed,
  onCollapse,
  onExpand,
}: ArticleRightPanelProps) => {
  const {
    copilotDisplayMode,
    setIsChatbotReady,
    setChatbotFlow,
    setUnreadMessagesCount,
    chatbotReferral,
    chatbotNodeId,
  } = useStore(state => state.copilotSlice);
  const { selectedSite } = useStore(state => state.siteSelectorSlice);
  const { t } = useTranslation();

  const isActive =
    copilotDisplayMode === CopilotDisplayMode.Chatbot ||
    copilotDisplayMode === CopilotDisplayMode.Loading;

  const handleIframeMessage = useMemo(
    () =>
      createIframeMessageHandler({
        goBack: noop,
        setShowRightSideBar: () => onCollapse(),
        setIsChatbotReady,
        selfArticleRedirect,
        setChatbotFlow,
        setUnreadMessagesCount,
        chatbotReferral,
        chatbotNodeId,
        selectedSiteMetaSiteId: selectedSite?.metaSiteId,
      }),
    [
      onCollapse,
      setIsChatbotReady,
      selfArticleRedirect,
      setChatbotFlow,
      setUnreadMessagesCount,
      chatbotReferral,
      chatbotNodeId,
      selectedSite,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  const isExpanded = isActive && !isCollapsed;
  const showCollapsed = isActive && isCollapsed;

  return (
    <div
      className={classNames(css.wrapper, {
        [css.expanded]: isExpanded,
        [css.collapsed]: showCollapsed,
      })}
    >
      {isActive && (
        <>
          <div className={classNames(css.iframeContainer, { [css.hidden]: isCollapsed })}>
            <iframe
              src={CHATBOT_IFRAME_URL}
              className={css.chatbotIframe}
              id="chatbot-iframe"
            />
          </div>
          {isCollapsed && (
            <div className={css.collapsedPanel}>
              <Tooltip content={t('article-page.right-panel.tooltip')} placement="top">
                <IconButton
                  size="small"
                  skin="dark"
                  priority="secondary"
                  onClick={onExpand}
                >
                  <ChatIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </div>
  );
};
